import {Fragment, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {Header, NAME_PLACEHOLDER} from '../Components/Pages/QueryTool';
import {GuidUtils, ObjectUtils} from '../Utils';
import {useDebouncedEffect, useUpdateEffect} from './EffectHooks';
import {useFormatValue} from './FormatterHooks';
import {
    DELETE_COLUMNS,
    FINISH_DELETE_COLUMNS,
    FINISH_INSERT_COLUMNS,
    INSERT_COLUMNS,
    SET_COLUMNS,
    SET_IS_STALE,
    SET_NEW_COLUMNS,
    SET_EDIT_COLUMN,
    SET_CHECKED,
    SET_EXPANDED,
} from '../Redux/Actions/Types/queryToolActionTypes';
import {useGetPlaceholderGuid, useQueryData} from './QueryToolDataHooks';
import {QueryToolUtils} from '../Utils';
import {ColumnCell} from '../Components/Pages/QueryTool/Table/ColumnCell';

const mapNodesToCheckboxTree = (nodes) => nodes.map(node => {
    const children = mapNodesToCheckboxTree(node.children ?? []);
    const {title, description, showGroupIcon, showFilterIcon} = node.labelParts ?? {};

    return {
        value: node.value,
        label: (
            <Fragment>
                <span className="tree-title">{title}</span>{' '}
                <span className="tree-description">{description}</span>
                {showGroupIcon ? <img className="group" src="/img/group.svg" width={12} height={12} alt="group" /> : null}
                {showFilterIcon ? <img className="filter" src="/img/filter.svg" width={12} height={12} alt="filter" /> : null}
            </Fragment>
        ),
        className: node.className,
        ...(children.length ? {children} : {}),
    };
});

export const useCreateColumn = () => {
    const numericSort = (a, b) => Number(a) - Number(b);

    return (columnData) => {
        columnData = {
            ...columnData,
            guid: columnData.guid || GuidUtils.Generate(),
        };
        
        return {
            guid: columnData.guid,
            data: columnData,
            accessorKey: columnData.guid,
            sortMethod: (columnData.dataType === 'int' || columnData.dataType === 'float') ? numericSort : undefined,
            header: Header,
            cell: ColumnCell,
        };
    };
};

export const useUpdateColumn = () => {
    const dispatch = useDispatch();
    const createColumnObject = useCreateColumn();

    const {columns, newColumns} = useSelector(state => state.queryToolState);

    const updateColumn = (guid, data, columnName = 'columns', isStale = false) => {
        const columnData = columnName === 'columns' ? columns : newColumns;
        const updatedColumns = columnData.map((x) => {
            if (x.data.guid === guid) {
                data = {...data, guid};
                return createColumnObject(data);
            }

            return x;
        });

        if (columnName === 'newColumns') {
            dispatch({type: SET_NEW_COLUMNS, newColumns: updatedColumns});
            updateColumn(guid, data);
        } else {
            dispatch({type: SET_COLUMNS, columns: updatedColumns});
        }
        dispatch({type: SET_IS_STALE, isStale});
    };

    return updateColumn;
};

export const useInsertColumns = () => {
    const dispatch = useDispatch();
    const updateColumn = useUpdateColumn();
    const createColumnObject = useCreateColumn();

    const {columns, newColumns, isInsertingColumns} = useSelector(state => state.queryToolState);

    const [firstColumn, setFirstColumn] = useState(null);
    const hasRunEffectRef = useRef(false);

    useUpdateEffect(() => {
        if (isInsertingColumns && firstColumn && !hasRunEffectRef.current) {
            const placeholder = newColumns.find((x) => NAME_PLACEHOLDER === x.data.name);

            if (placeholder) {
                updateColumn(placeholder.data.guid, firstColumn, 'newColumns');
            }

            setFirstColumn(null);
            dispatch({type: FINISH_INSERT_COLUMNS});
            hasRunEffectRef.current = true;
        } else if (!isInsertingColumns) {
            hasRunEffectRef.current = false;
        }

        return () => {
            hasRunEffectRef.current = false;
        };
    }, [dispatch, isInsertingColumns, firstColumn, newColumns, updateColumn]);

    return (guid, columnData) => {
        const placeholder = newColumns.find((x) => NAME_PLACEHOLDER === x.data.name);
        const columnsClone = ObjectUtils.DeepClone(columns);
        const index = columnsClone.findIndex((x) => guid === x.data.guid);
        const nextIndex = index + (newColumns.length || 1);

        const [first, ...rest] = columnData;
        let columnsToInsert = columnData;
        if (placeholder) {
            columnsToInsert = rest;
        }
        setFirstColumn(first);
        const newColumnObjects = columnsToInsert.map(x => createColumnObject(x));

        columnsClone.splice(nextIndex, 0, ...newColumnObjects);

        dispatch({
            type: INSERT_COLUMNS,
            columns: columnsClone,
            newColumns: [...newColumns, ...newColumnObjects],
        });
    };
};

export const useDeleteColumns = () => {
    const dispatch = useDispatch();
    const updateColumn = useUpdateColumn();
    const getPlaceholderGuid = useGetPlaceholderGuid();
    const queryData = useQueryData();

    const {columns, newColumns, isDeletingColumns} = useSelector(state => state.queryToolState);

    const [shouldResetPlaceholder, setShouldResetPlaceholder] = useState(false);
    const [shouldUpdate, setShouldUpdate] = useState(false);

    useEffect(() => {
        if (!isDeletingColumns) {
            return;
        }

        if (shouldResetPlaceholder) {
            updateColumn(getPlaceholderGuid, {name: NAME_PLACEHOLDER}, 'newColumns');
        }

        if (shouldUpdate) {
            queryData();
        }

        setShouldResetPlaceholder(false);
        setShouldUpdate(false);
        dispatch({type: FINISH_DELETE_COLUMNS});
    }, [dispatch, getPlaceholderGuid, isDeletingColumns, queryData, shouldResetPlaceholder, shouldUpdate, updateColumn]);

    return (guids, resetPlaceholder = false, shouldQueryUpdate = false) => {
        let [, ...rest] = guids;

        let idsToDelete = guids;
        if (resetPlaceholder) {
            idsToDelete = rest;
        }

        setShouldResetPlaceholder(resetPlaceholder);
        setShouldUpdate(shouldQueryUpdate);
        dispatch({
            type: DELETE_COLUMNS,
            columns: columns.filter(x => !idsToDelete.includes(x.data.guid)),
            newColumns: newColumns.filter(x => !idsToDelete.includes(x.data.guid)),
        });
    };
};

export const useFindColumnById = () => {
    const {availableColumnSources} = useSelector(state => state.queryToolState);

    return (id) => {
        return availableColumnSources
            .map(x => x.ColumnSource)
            .flat(1)
            .find((x) => id == x.name);
    };
};

export const useColumnEditor = (guid) => {
    const dispatch = useDispatch();
    const queryData = useQueryData();
    const updateColumn = useUpdateColumn();
    const deleteColumns = useDeleteColumns();
    const placeholderGuid = useGetPlaceholderGuid();

    const {
        columns,
        newColumns,
        filterMethods,
        groupingMethods,
        useGroupBy,
        editColumn,
        sorted,
        insertColumnId,
    } = useSelector(state => state.queryToolState);

    const columnEntry = columns.find(x => x.guid === guid);
    const columnData = columnEntry?.data ?? {};
    const columnIndex = columns.findIndex(x => x.guid === guid);

    const computeAggregate = useCallback((data) => (
        data?.aggregate != null ? data.aggregate : data?.groupingMethodId || ''
    ), []);

    const [title, setTitle] = useState('');
    const [filters, setFilters] = useState([]);
    const [groupingValue, setGroupingValue] = useState('');

    useEffect(() => {
        setTitle(columnData?.title ?? '');
        setFilters([...(columnData?.filters ?? [])]);
        setGroupingValue(computeAggregate(columnData));
    }, [columnData, computeAggregate]);

    const updateColumnData = useCallback((updatedColumnData, isStale = false) => {
        const updated = {...columnData, ...updatedColumnData};
        updateColumn(guid, updated, 'columns', isStale);
    }, [columnData, guid, updateColumn]);

    const addFilter = useCallback(() => {
        const defaultFilter = filterMethods.find(x => x.code === 'equals');
        if (!defaultFilter) {
            return;
        }

        const newFilter = {
            ...defaultFilter,
            guid: GuidUtils.Generate(),
            value: '',
        };

        setFilters(prevFilters => [...prevFilters, newFilter]);
    }, [filterMethods]);

    const updateFilterMethod = useCallback((filterGuid, filterMethodId) => {
        const changedFilter = filterMethods.find(x => x.filterMethodId === Number(filterMethodId));
        if (!changedFilter) {
            return;
        }

        setFilters(prevFilters => prevFilters.map(filter => (
            filter.guid === filterGuid
                ? {
                    ...filter,
                    ...changedFilter,
                    filterMethodId: Number(filterMethodId),
                }
                : filter
        )));
    }, [filterMethods]);

    const updateFilterValue = useCallback((filterGuid, value) => {
        setFilters(prevFilters => prevFilters.map(filter => (
            filter.guid === filterGuid ? {...filter, value} : filter
        )));
    }, []);

    const removeFilter = useCallback((filterGuid) => {
        setFilters(prevFilters => prevFilters.filter(filter => filter.guid !== filterGuid));
    }, []);

    const handleTitleChange = useCallback((value) => {
        setTitle(value);
    }, []);

    const handleGroupingChange = useCallback((value) => {
        setGroupingValue(value);
    }, []);

    const handleDeleteColumn = useCallback(() => {
        const shouldResetGroupBy = useGroupBy && columnData?.groupingMethodId === null;
        deleteColumns([guid], false, shouldResetGroupBy);
        dispatch({type: SET_EDIT_COLUMN, editColumn: null});
    }, [columnData, deleteColumns, dispatch, guid, useGroupBy]);

    const isEditing = editColumn?.guid === guid;

    const openEditor = useCallback(() => {
        dispatch({type: SET_EDIT_COLUMN, editColumn: columnData});
    }, [columnData, dispatch]);

    const cancelEdit = useCallback(() => {
        if (editColumn) {
            updateColumn(guid, editColumn);
        }
        dispatch({type: SET_EDIT_COLUMN, editColumn: null});
    }, [dispatch, editColumn, guid, updateColumn]);

    const saveColumn = useCallback(() => {
        const originalFilters = columnData?.filters ?? [];
        const filtersChanged = !ObjectUtils.Compare(originalFilters, filters);
        const originalGrouping = computeAggregate(columnData);
        const groupingChanged = (groupingValue || '') !== (originalGrouping || '');
        const dataChanged = filtersChanged || groupingChanged;

        const updatedColumnData = {
            title,
            filters: filters.map(filter => ({...filter})),
            aggregate: useGroupBy ? (groupingValue || null) : null,
        };

        dispatch({type: SET_EDIT_COLUMN, editColumn: null});

        updateColumnData(updatedColumnData, dataChanged);

        if (dataChanged) {
            queryData();
        }
    }, [columnData, dispatch, filters, groupingValue, queryData, title, updateColumnData, useGroupBy]);

    const isPlaceholder = placeholderGuid === guid;
    const isNewColumn = newColumns.some(x => x.data.guid === guid);
    const hasFilters = Boolean(columnData?.filters?.length);
    const sortState = sorted.find(x => x.id === guid);
    const sortDirection = !sortState ? null : (sortState.desc ? 'desc' : 'asc');
    const indicator = useMemo(() => {
        if (useGroupBy && !(computeAggregate(columnData))) {
            return 'group';
        }

        if (hasFilters) {
            return 'filter';
        }

        return 'edit';
    }, [columnData, computeAggregate, hasFilters, useGroupBy]);

    const popoverController = useMemo(() => ({
        filterMethods,
        groupingMethods,
        useGroupBy,
        filters,
        addFilter,
        updateFilterMethod,
        updateFilterValue,
        removeFilter,
        title,
        onTitleChange: handleTitleChange,
        groupingValue,
        onGroupingChange: handleGroupingChange,
        onDeleteColumn: handleDeleteColumn,
        onCancel: cancelEdit,
        onSave: saveColumn,
    }), [addFilter, cancelEdit, filters, filterMethods, groupingMethods, groupingValue, handleDeleteColumn, handleGroupingChange, handleTitleChange, removeFilter, saveColumn, title, updateFilterMethod, updateFilterValue, useGroupBy]);

    return {
        column: columnData,
        columnIndex,
        isFirstColumn: columnIndex === 0,
        isLastColumn: columnIndex === columns.length - 1,
        isPlaceholder,
        isNewColumn,
        hasFilters,
        isEditing,
        sortDirection,
        indicator,
        insertColumnId,
        openEditor,
        cancelEdit,
        saveColumn,
        deleteColumn: handleDeleteColumn,
        popoverController,
    };
};

export const useAddColumnController = ({saveNewColumns}) => {
    const dispatch = useDispatch();
    const placeholderGuid = useGetPlaceholderGuid();
    const findColumnById = useFindColumnById();
    const insertColumns = useInsertColumns();
    const deleteColumns = useDeleteColumns();

    const {checked, expanded, availableColumnSources, newColumns} = useSelector(state => state.queryToolState);

    const nodes = useMemo(() => QueryToolUtils.buildColumnTree(availableColumnSources), [availableColumnSources]);
    const [nodesFilteredRaw, setNodesFilteredRaw] = useState(nodes);
    const [filterText, setFilterText] = useState('');
    const lastFilterTextRef = useRef('');

    useEffect(() => {
        setNodesFilteredRaw(nodes);
    }, [nodes]);

    useDebouncedEffect(() => {
        const {filteredNodes, expandedByFilter} = QueryToolUtils.filterColumnTree(nodes, filterText);
        setNodesFilteredRaw(filteredNodes);

        const filterChanged = filterText !== lastFilterTextRef.current;

        if (filterChanged && filterText && expandedByFilter.length) {
            const mergedExpanded = [...new Set([...expanded, ...expandedByFilter])];
            dispatch({type: SET_EXPANDED, expanded: mergedExpanded});
        }

        lastFilterTextRef.current = filterText;
    }, [dispatch, expanded, filterText, nodes], 250);

    const nodesFiltered = useMemo(() => mapNodesToCheckboxTree(nodesFilteredRaw), [nodesFilteredRaw]);

    const handleFilterChange = useCallback((event) => {
        const value = event.target.value;
        setFilterText(value);
        if (value === '') {
            dispatch({type: SET_EXPANDED, expanded: []});
        }
    }, [dispatch]);

    const handleCheck = useCallback((checkedArray) => {
        const added = checkedArray
            .filter(id => !checked.includes(id))
            .map(id => findColumnById(id));
        const deleted = checked
            .filter(id => !checkedArray.includes(id))
            .map(id => {
                const column = newColumns.find(x => x?.data?.name === id);
                return column?.data?.guid;
            })
            .filter(Boolean);
        const shouldResetPlaceholder = deleted.length === newColumns.length;

        if (added.length > 0) insertColumns(placeholderGuid, added);
        if (deleted.length > 0) deleteColumns(deleted, shouldResetPlaceholder);

        dispatch({type: SET_CHECKED, checked: checkedArray});
    }, [checked, deleteColumns, dispatch, findColumnById, insertColumns, newColumns, placeholderGuid]);

    const handleExpand = useCallback((expandedNodes) => {
        dispatch({type: SET_EXPANDED, expanded: expandedNodes});
    }, [dispatch]);

    const handleSave = useCallback(() => {
        if (!placeholderGuid) {
            return;
        }
        saveNewColumns(placeholderGuid);
    }, [placeholderGuid, saveNewColumns]);

    return {
        placeholderGuid,
        checked,
        expanded,
        nodesFiltered,
        filterText,
        handleFilterChange,
        handleCheck,
        handleExpand,
        handleSave,
    };
};
